import { SITE_URL, SOCIAL_IMAGE_URL } from '@/lib/brand';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { BRAND_NAME } from '@/config/brand';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object;
  noIndex?: boolean;
  /** The robots meta content when `noindex, nofollow` is not the one wanted (the ad landing pages are `noindex, follow`); wins over `noIndex`. */
  robots?: string;
}

/**
 * SEOHead - Comprehensive SEO meta tags component
 *
 * Features:
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Structured data (JSON-LD)
 * - Canonical URLs
 * - Robots meta tags
 *
 * @example
 * <SEOHead
 *   title="Online Booking for Salons | Scopie"
 *   description="Streamline your salon with Scopie..."
 *   keywords="salon appointment booking, salon scheduling software"
 *   canonical="https://getscopie.com/for-salons"
 *   ogImage="https://getscopie.com/images/og-salons.jpg"
 *   structuredData={salonsSchema}
 * />
 */
export const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = SOCIAL_IMAGE_URL,
  ogType = 'website',
  structuredData,
  noIndex = false,
  robots,
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const fullTitle = `${title} | ${BRAND_NAME}`;
  const fullCanonical = canonical?.replace(/^https:\/\/getscopie\.com(?=\/|$)/, SITE_URL) || SITE_URL;

  // Get og:locale based on current language
  const ogLocale =
    i18n.language === 'pt-BR' ? 'pt_BR'
    : i18n.language?.startsWith('es') ? 'es_LA'
    : 'en_US';

  return (
    <Helmet>
      {/* <html lang> is kept in sync with the UI language globally (src/i18n/config.ts) */}
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      {robots ? <meta name="robots" content={robots} /> : noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={BRAND_NAME} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
